import axios from 'axios'
import _ from 'lodash'
import React, { useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'

import statesAndCities from '@/assets/statesAndCities.json'
import Divider from '@/components/Divider'
import { maskCpf, maskPhone, unMask } from '@/components/Mask'
import CitySearchSelect from '@/components/CitySearchSelect'
import { CloseOutlined as CloseIcon, ImageOutlined as ImageOutlinedIcon } from '@mui/icons-material'
import {
    Grid,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogActions,
    CircularProgress,
    FormControl,
    MenuItem,
    Select,
    InputLabel,
    Checkbox,
    FormControlLabel,
    Box,
    Chip,
    Typography,
    IconButton
} from '@mui/material'

import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'

import useStyles from '../../pages/Intranet/Companies/styles'
import { CompanyType, AddressType } from '@/pages/Intranet/Companies'

interface CompanyFormDialogProps {
    open: boolean
    company: CompanyType
    setCompany: React.Dispatch<React.SetStateAction<CompanyType>>
    loading: boolean
    onClose: () => void
    onSave: () => void
}

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Grid item xs={12}>
        <Box
            display="flex"
            alignItems="center"
            sx={{
                mt: 2,
                mb: 1,
                gap: 1.5
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    whiteSpace: 'nowrap'
                }}
            >
                {children}
            </Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
        </Box>
    </Grid>
)

const SubsectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Grid item xs={12}>
        <Typography
            variant="caption"
            sx={{
                display: 'block',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: 'text.disabled',
                mt: 1,
                mb: 0.5
            }}
        >
            {children}
        </Typography>
    </Grid>
)

// TLDs compostos (ex.: .com.br, .net.br, .gov.br) — preserva a raiz correta
// ao remover subdomínios do campo "Site"
const compoundTlds = new Set([
    'com', 'net', 'org', 'gov', 'edu', 'mil', 'jus', 'ac', 'co',
    'art', 'fm', 'radio', 'am', 'blog', 'biz', 'emp', 'ind',
    'inf', 'jor', 'leilao', 'mus', 'pro', 'rec', 'srv', 'tec', 'tur', 'tv', 'wiki'
])

// Normaliza o site para o padrão salvo no backend: sem protocolo (http/https)
// e sem subdomínio (ex.: "https://www.loopert.com" → "loopert.com")
const normalizeSite = (value: string): string => {
    const site = value.trim()
    if (!site) return site

    const withoutProtocol = site.replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/+$/, '')
    if (!withoutProtocol) return withoutProtocol

    const parts = withoutProtocol.split('.')
    const secondLevel = parts[parts.length - 2]?.toLowerCase()
    const hasCompoundTld = parts.length > 2 && !!secondLevel && compoundTlds.has(secondLevel)
    const keepCount = hasCompoundTld ? 3 : 2

    return parts.length > keepCount ? parts.slice(-keepCount).join('.') : withoutProtocol
}

const CompanyFormDialog: React.FC<CompanyFormDialogProps> = ({
    open,
    company,
    setCompany,
    loading,
    onClose,
    onSave
}) => {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()

    const [uploadedLogo, setUploadedLogo] = useState<File | undefined>()
    const [inputValue, setInputValue] = useState('')
    const [trialDays, setTrialDays] = useState<15 | 30>(30)

    // Reseta o logo local sempre que o dialog é reaberto para uma empresa diferente/nova
    useEffect(() => {
        if (open) setUploadedLogo(undefined)
    }, [open, company.uuid])

    // Sincroniza a duração do trial ao abrir o dialog
    useEffect(() => {
        if (open && company?.status === 'trial' && company.trialExpireAt) {
            const date = company.trialExpireAt instanceof Date ? company.trialExpireAt : new Date(company.trialExpireAt)
            const daysDiff = Math.round((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
            setTrialDays(daysDiff >= 30 ? 30 : 15)
        } else {
            setTrialDays(30)
        }
    }, [open, company.uuid, company?.status, company?.trialExpireAt])

    const geocodeAddress = async (address: AddressType): Promise<{ latitude: string; longitude: string } | null> => {
        try {
            const addressString = `${address.street || ''} ${address.number || ''}, ${address.neighborhood || ''}, ${address.city || ''}, ${address.state || ''}, Brasil`.trim()

            const response = await axios.get('https://photon.komoot.io/api/', {
                params: {
                    q: addressString,
                    limit: 1
                }
            })

            if (response.data.features && response.data.features.length > 0) {
                const coords = response.data.features[0].geometry.coordinates
                return {
                    longitude: coords[0].toString(),
                    latitude: coords[1].toString()
                }
            }

            return null
        } catch (error) {
            console.error('Erro ao buscar coordenadas:', error)
            return null
        }
    }

    const handleZipCodeInput = async (zipcode: string) => {
        try {
            setCompany({ ...company, address: { ...company?.address, zipcode: zipcode } })

            if (zipcode.length === 8) {
                const { data } = await axios.get(`https://viacep.com.br/ws/${zipcode}/json/`)

                if (data && !data.erro) {
                    const state = _.find(statesAndCities, { initials: data.uf })?.name

                    const updatedAddress = {
                        ...company?.address,
                        zipcode: zipcode,
                        street: data.logradouro || company.address?.street,
                        neighborhood: data.bairro || company.address?.neighborhood,
                        state: state || company.address?.state,
                        city: data.localidade || company.address?.city,
                        number: company.address?.number || '',
                        latitude: company.address?.latitude || null,
                        longitude: company.address?.longitude || null
                    }

                    setCompany({
                        ...company,
                        address: updatedAddress
                    })

                    // Buscar coordenadas automaticamente se tiver endereço completo
                    if (data.localidade && state) {
                        const coords = await geocodeAddress(updatedAddress)
                        if (coords) {
                            setCompany({
                                ...company,
                                address: {
                                    ...updatedAddress,
                                    latitude: coords.latitude,
                                    longitude: coords.longitude
                                }
                            })
                            enqueueSnackbar('Coordenadas obtidas automaticamente!', { variant: 'success' })
                        }
                    }
                }
            }
        } catch (e) {
            console.error('Erro ao buscar CEP:', e)
        }
    }

    const handleSelectLogo = async (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            const file = event.target.files?.[0]
            if (!file) return

            const validFormats = ['image/png', 'image/jpeg', 'image/jpg']
            if (!validFormats.includes(file.type)) {
                enqueueSnackbar('Formato inválido. Apenas PNG, JPEG e JPG são permitidos.', { variant: 'warning' })
                return
            }

            const fileBase64: string = await new Promise((resolve) => {
                const reader = new FileReader()
                reader.readAsDataURL(file)

                reader.onloadend = (evt) => {
                    if (evt?.target?.readyState === FileReader.DONE) {
                        resolve(evt?.target?.result as string)
                    }
                }
            })

            setCompany({ ...company, logoBase64: String(fileBase64) })
            setUploadedLogo(file)
        } catch (error) {
            enqueueSnackbar('Ocorreu um erro ao tentar fazer upload da imagem.', { variant: 'warning' })
        }
    }

    const handleRemoveLogo = (e: React.MouseEvent) => {
        e.stopPropagation()
        setUploadedLogo(undefined)
        setCompany({
            ...company,
            logoBase64: null,
            logoUrl: undefined
        })
    }

    const handleCityChange = (value: string[]) => {
        setCompany({
            ...company,
            coveredCities: value as string[]
        })
    }

    const handleStarClick = (value: string) => {
        const updatedCities = [value, ...company?.coveredCities?.filter((city) => city !== value) || []]
        setCompany({
            ...company,
            coveredCities: updatedCities
        })
    }

    const handleAddAdsolutionId = () => {
        const value = inputValue.trim()
        if (!value) return

        setCompany((prev) => ({
            ...prev,
            adsolutionIds: [...(prev.adsolutionIds || []), value]
        }))
        setInputValue('')
    }

    const handleDeleteAdsolutionId = (idToDelete: string) => {
        setCompany((prev) => ({
            ...prev,
            adsolutionIds: (prev.adsolutionIds || []).filter((id) => id !== idToDelete)
        }))
    }

    const handleSiteBlur = () => {
        const normalized = normalizeSite(company.site || '')
        if (normalized === company.site) return

        setCompany({ ...company, site: normalized })
        enqueueSnackbar(`Site normalizado para o padrão: ${normalized}`, { variant: 'success' })
    }

    return (
        <Dialog
            open={open}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: '12px' }
            }}
        >
            <DialogTitle className={classes.dialogTitle} sx={{ pr: 6, position: 'relative' }}>
                {!company?.uuid ? 'Criando nova ' : 'Editando a '}rádio
                <IconButton
                    aria-label="Fechar"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: 'text.secondary'
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Grid container className={classes.companyDialogContainer} sx={{ px: 3 }}>

                <SectionTitle>Dados Básicos</SectionTitle>

                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="company_name_field"
                        className={classes.itemInput}
                        label="Nome Fantasia" size="small" color="primary" variant="outlined"
                        value={company.name}
                        onChange={({ target }) => setCompany({ ...company, name: target.value })}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="company_frequency_field"
                        className={classes.itemInput}
                        label="Frequência" size="small" color="primary" variant="outlined"
                        value={company.frequency}
                        onChange={({ target }) => setCompany({ ...company, frequency: target.value })}
                        inputProps={{
                            'data-lpignore': 'true',
                            'data-1password': 'true',
                            'data-op-ignore': 'true'
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                id="radio_enabled_checkbox"
                                checked={company.enabled}
                                onChange={({ target }) => setCompany({ ...company, enabled: target.checked })}
                                color="default"
                            />
                        }
                        label={'Radio Ativa'}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                id="concurrence_enabled_checkbox"
                                checked={company.concurrenceEnabled}
                                onChange={({ target }) => setCompany({ ...company, concurrenceEnabled: target.checked })}
                                color="default"
                            />
                        }
                        label={'Concorrência Pública Ativa'}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput} style={{ marginTop: -16 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!!company?.whatsappHasSuspended}
                                onChange={({ target }) => setCompany({ ...company, whatsappHasSuspended: target.checked })}
                                color="primary"
                            />
                        }
                        label="Já foi suspenso do Whatsapp"
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput} style={{ marginTop: -16 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!!company?.adrCoreDisabled}
                                onChange={({ target }) => setCompany({ ...company, adrCoreDisabled: target.checked })}
                                color="default"
                            />
                        }
                        label="AD+R Core desabilitado"
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <FormControl className={classes.formControl}>
                        <InputLabel className={classes.selectInput} htmlFor='campaign_dial_limit'>Plano Campanhas Dial</InputLabel>
                        <Select
                            onChange={({ target }) => {
                                setCompany({
                                    ...company,
                                    campaignDialMonthLimit: target.value === 'unlimited'
                                        ? null
                                        : (typeof target.value === 'number' && target.value > 0
                                            ? target.value
                                            : null)
                                })
                            }}
                            id="campaign_dial_limit"
                            label="Plano campanhas dial"
                            variant="outlined"
                            color="primary"
                            className={classes.select}
                            value={company?.campaignDialMonthLimit === null ? 'unlimited' : company?.campaignDialMonthLimit}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 400
                                    }
                                }
                            }}
                        >
                            <MenuItem value="unlimited">Desativado</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                            <MenuItem value={250}>250</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <FormControl className={classes.formControl}>
                        <InputLabel className={classes.selectInput} htmlFor='company_status_select'>Status da Empresa</InputLabel>
                        <Select
                            onChange={({ target }) => {
                                const newStatus = target.value as 'trial' | 'internal' | 'customer' | 'concurrence'

                                let newTrialExpireAt = company.trialExpireAt
                                if (newStatus === 'trial') {
                                    if (!company.trialExpireAt) {
                                        const date = new Date()
                                        date.setDate(date.getDate() + trialDays)
                                        newTrialExpireAt = date
                                    }
                                } else {
                                    newTrialExpireAt = undefined
                                }

                                setCompany({
                                    ...company,
                                    status: newStatus,
                                    trialExpireAt: newTrialExpireAt
                                })
                            }}
                            id="company_status_select"
                            label="Status da Empresa"
                            variant="outlined"
                            color="primary"
                            className={classes.select}
                            value={company?.status || ''}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 400
                                    }
                                }
                            }}
                        >
                            <MenuItem value="trial">Período de Avaliação</MenuItem>
                            <MenuItem value="internal">Interna</MenuItem>
                            <MenuItem value="customer">Pagante</MenuItem>
                            <MenuItem value="concurrence">Concorrência</MenuItem>
                            <MenuItem value="expired" disabled>Expirado</MenuItem>
                            <MenuItem value="deleted" disabled>Deletado</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <FormControl className={classes.formControl}>
                        <InputLabel className={classes.selectInput} htmlFor='company_plan_select'>Plano</InputLabel>
                        <Select
                            onChange={({ target }) => {
                                setCompany({
                                    ...company,
                                    plan: target.value as 'basic' | 'free' | 'full'
                                })
                            }}
                            id="company_plan_select"
                            label="Plano"
                            variant="outlined"
                            color="primary"
                            className={classes.select}
                            value={company?.plan || 'free'}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 400
                                    }
                                }
                            }}
                        >
                            <MenuItem value="free">Gratuito</MenuItem>
                            <MenuItem value="basic">Básico</MenuItem>
                            <MenuItem value="full">Completo</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                {(company?.status === 'trial' || company?.status === 'customer') && (
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                            <DatePicker
                                label={company?.status === 'trial' ? 'Data de Expiração do Trial' : 'Data de expiração da assinatura'}
                                value={company.trialExpireAt ? dayjs(company.trialExpireAt) : null}
                                onChange={(newValue) => {
                                    setCompany({
                                        ...company,
                                        trialExpireAt: newValue ? newValue.toDate() : undefined
                                    })
                                }}
                                slotProps={{
                                    textField: {
                                        id: 'trial_expire_at_field',
                                        className: classes.itemInput,
                                        size: 'small',
                                        color: 'primary',
                                        variant: 'outlined',
                                        fullWidth: true
                                    }
                                }}
                            />
                        </LocalizationProvider>
                        {company?.status === 'trial' && (
                            <Box display="flex" gap={1} mt={1}>
                                <Button
                                    id="trial_15_days_button"
                                    size="small"
                                    variant={trialDays === 15 ? 'contained' : 'outlined'}
                                    color="primary"
                                    onClick={() => {
                                        const date = new Date()
                                        date.setDate(date.getDate() + 15)
                                        setTrialDays(15)
                                        setCompany({ ...company, trialExpireAt: date })
                                    }}
                                >
                                    15 dias
                                </Button>
                                <Button
                                    id="trial_30_days_button"
                                    size="small"
                                    variant={trialDays === 30 ? 'contained' : 'outlined'}
                                    color="primary"
                                    onClick={() => {
                                        const date = new Date()
                                        date.setDate(date.getDate() + 30)
                                        setTrialDays(30)
                                        setCompany({ ...company, trialExpireAt: date })
                                    }}
                                >
                                    30 dias
                                </Button>
                            </Box>
                        )}
                    </Grid>
                )}

                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <FormControl className={classes.formControl}>
                        <InputLabel className={classes.selectInput} htmlFor='company_referral_partner_select'>Indicação de parceiro</InputLabel>
                        <Select
                            onChange={({ target }) => {
                                setCompany({
                                    ...company,
                                    referralPartner: target.value as 'ACERT' | 'ADR' | 'Loopert'
                                })
                            }}
                            id="company_referral_partner_select"
                            label="Indicação de parceiro"
                            variant="outlined"
                            color="primary"
                            className={classes.select}
                            value={company?.referralPartner || ''}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 400
                                    }
                                }
                            }}
                        >
                            <MenuItem value="Loopert">Loopert</MenuItem>
                            <MenuItem value="ACERT">ACERT</MenuItem>
                            <MenuItem value="ADR">ADR</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <FormControl className={classes.formControl}>
                        <InputLabel className={classes.selectInput} htmlFor='segments_select'>Segmentos</InputLabel>
                        <Select
                            onChange={({ target }) => {
                                setCompany({
                                    ...company,
                                    segments: typeof target.value === 'string'
                                        ? [target.value]
                                        : target.value as string[]
                                })
                            }}
                            id="segments_select"
                            label="Segmentos"
                            variant="outlined"
                            color="primary"
                            multiple
                            className={classes.select}
                            value={company?.segments || []}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 400
                                    }
                                }
                            }}
                        >
                            <MenuItem value="Adulto Classic">Adulto Classic</MenuItem>
                            <MenuItem value="Adulto Contemporâneo">Adulto Contemporâneo</MenuItem>
                            <MenuItem value="Eclética">Eclética</MenuItem>
                            <MenuItem value="Esportes">Esportes</MenuItem>
                            <MenuItem value="Entrevistas">Entrevistas</MenuItem>
                            <MenuItem value="Flashback">Flashback</MenuItem>
                            <MenuItem value="Gospel Católica">Gospel Católica</MenuItem>
                            <MenuItem value="Gospel Evangélica">Gospel Evangélica</MenuItem>
                            <MenuItem value="Hits">Hits</MenuItem>
                            <MenuItem value="Jornalismo (News)">Jornalismo (News)</MenuItem>
                            <MenuItem value="MPB">MPB</MenuItem>
                            <MenuItem value="Popular">Popular</MenuItem>
                            <MenuItem value="Pop ">Pop </MenuItem>
                            <MenuItem value="Pop-Rock">Pop-Rock</MenuItem>
                            <MenuItem value="Sertanejo">Sertanejo</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <SectionTitle>Logo e Link Externo</SectionTitle>

                <Grid item xs={12} sm={12} className={classes.dialogItemInput}>
                    <input
                        id="logo-selector"
                        type="file"
                        accept=".png, .jpeg, .jpg"
                        onChange={handleSelectLogo}
                        multiple={false}
                        style={{ display: 'none' }}
                    />
                    <Box
                        onClick={() => document.getElementById('logo-selector')?.click()}
                        sx={{
                            border: '1.5px dashed',
                            borderColor: 'divider',
                            borderRadius: '10px',
                            p: 2.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2.5,
                            cursor: 'pointer',
                            transition: 'border-color 0.2s, background-color 0.2s',
                            '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: 'action.hover'
                            }
                        }}
                    >
                        <Box
                            sx={{
                                position: 'relative',
                                width: 140,
                                height: 140,
                                flexShrink: 0,
                                borderRadius: '10px',
                                overflow: 'hidden',
                                backgroundColor: '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid',
                                borderColor: 'divider'
                            }}
                        >
                            {(uploadedLogo || company?.logoUrl) ? (
                                <>
                                    <img
                                        src={uploadedLogo ? URL.createObjectURL(uploadedLogo) : `${company?.logoUrl}?t=${Date.now()}`}
                                        alt="Logo Preview"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={(e) => handleRemoveLogo(e)}
                                        sx={{
                                            position: 'absolute',
                                            top: 4,
                                            right: 4,
                                            width: 26,
                                            height: 26,
                                            backgroundColor: 'rgba(0,0,0,0.55)',
                                            color: '#fff',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0,0,0,0.75)'
                                            }
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </>
                            ) : (
                                <ImageOutlinedIcon sx={{ color: 'text.disabled', fontSize: 48 }} />
                            )}
                        </Box>
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                {(uploadedLogo || company?.logoUrl) ? 'Trocar logo' : 'Escolher logo'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {uploadedLogo ? uploadedLogo.name : 'PNG, JPEG ou JPG'}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={12} sm={12} className={classes.dialogItemInput}>
                    <TextField
                        id="external_icecast_url_field"
                        className={classes.itemInput}
                        label="Link externo do Icecast (Concorrencia) (Ex. /status.xml)" size="small" color="primary" variant="outlined"
                        value={company.externalIcecastUrl}
                        onChange={({ target }) => setCompany({ ...company, externalIcecastUrl: target.value })}
                    />
                </Grid>

                <SectionTitle>Integrações</SectionTitle>

                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="rh_radio_uuid_field"
                        className={classes.itemInput}
                        label="UUID (Rádio Health)" size="small" color="primary" variant="outlined"
                        value={company.rhRadioUuid}
                        onChange={({ target }) => setCompany({ ...company, rhRadioUuid: target.value })}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="rh_recorder_uuid_field"
                        className={classes.itemInput}
                        label="Recorder UUID (Rádio Health)" size="small" color="primary" variant="outlined"
                        value={company.rhRecorderUuid}
                        onChange={({ target }) => setCompany({ ...company, rhRecorderUuid: target.value })}
                    />
                </Grid>

                <Grid item xs={12} className={classes.dialogItemInput}>
                    <TextField
                        id="url_radios_com_br_field"
                        className={classes.itemInput}
                        label="Link radios.com.br"
                        placeholder="https://radios.com.br/"
                        size="small"
                        color="primary"
                        variant="outlined"
                        fullWidth
                        value={company.urlRadiosComBr || ''}
                        onChange={({ target }) => setCompany({ ...company, urlRadiosComBr: target.value })}
                    />
                </Grid>

                <Grid item xs={12} className={classes.dialogItemInput}>
                    <TextField
                        label="IDs do Adsolution"
                        placeholder="Digite Ex: 1236 e pressione Enter"
                        value={inputValue}
                        onChange={(e) => {
                            const value = e.target.value
                            if (/^\d*$/.test(value)) {
                                setInputValue(value)
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ',') {
                                e.preventDefault()
                                handleAddAdsolutionId()
                            }
                        }}
                        inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*'
                        }}
                        fullWidth
                        size="small"
                    />
                    <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                        {(company.adsolutionIds || []).map((id, index) => (
                            <Chip
                                key={index}
                                label={id}
                                onDelete={() => handleDeleteAdsolutionId(id)}
                            />
                        ))}
                    </Box>
                </Grid>

                <SectionTitle>Dados Fiscais</SectionTitle>

                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="corporate_name_field"
                        className={classes.itemInput}
                        label="Razão Social" size="small" color="primary" variant="outlined"
                        value={company.corporateName}
                        onChange={({ target }) => setCompany({ ...company, corporateName: target.value })}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="cnpj_cpf_field"
                        className={classes.itemInput}
                        label="CNPJ/CPF da rádio" size="small" color="primary" variant="outlined"
                        value={maskCpf(company.cnpjCpf)}
                        onChange={({ target }) => setCompany({ ...company, cnpjCpf: unMask(target.value) })}
                        inputProps={{ maxLength: 18 }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="cnpj_billing_field"
                        className={classes.itemInput}
                        label="CNPJ de Cobrança" size="small" color="primary" variant="outlined"
                        value={maskCpf(company.cnpjBilling)}
                        onChange={({ target }) => setCompany({ ...company, cnpjBilling: unMask(target.value) })}
                        inputProps={{ maxLength: 18 }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="fistel_field"
                        className={classes.itemInput}
                        label="Fistel" size="small" color="primary" variant="outlined"
                        value={company.fistel}
                        onChange={({ target }) => setCompany({ ...company, fistel: target.value })}
                    />
                </Grid>

                <SectionTitle>Contatos</SectionTitle>

                <SubsectionTitle>Rádio</SubsectionTitle>

                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="email_field"
                        className={classes.itemInput}
                        label="Email" size="small" color="primary" variant="outlined"
                        value={company.email}
                        onChange={({ target }) => setCompany({ ...company, email: target.value })}
                        inputProps={{
                            'data-lpignore': 'true',
                            'data-1password': 'true',
                            'data-op-ignore': 'true'
                        }}
                    InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="phone_number_field"
                        className={classes.itemInput}
                        label="Número de telefone" size="small" color="primary" variant="outlined"
                        value={maskPhone(company.phoneNumber)}
                        onChange={({ target }) => setCompany({ ...company, phoneNumber: unMask(target.value) })}
                        inputProps={{
                            maxLength: 16,
                            'data-lpignore': 'true',
                            'data-1password': 'true',
                            'data-op-ignore': 'true'
                        }}
                    InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="support_phone_field"
                        className={classes.itemInput}
                        label="Número de telefone do suporte" size="small" color="primary" variant="outlined"
                        value={maskPhone(company?.supportPhonenumber)}
                        onChange={({ target }) => setCompany({ ...company, supportPhonenumber: unMask(target.value) })}
                        inputProps={{
                            'data-lpignore': 'true',
                            'data-1password': 'true',
                            'data-op-ignore': 'true'
                        }}
                    InputLabelProps={{ shrink: true }}
                    />
                </Grid>

                <SubsectionTitle>OPEC</SubsectionTitle>

                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="email_opec_field"
                        className={classes.itemInput}
                        label="Email OPEC" size="small" color="primary" variant="outlined"
                        value={company.emailOpec}
                        onChange={({ target }) => setCompany({ ...company, emailOpec: target.value })}
                        inputProps={{
                            'data-lpignore': 'true',
                            'data-1password': 'true',
                            'data-op-ignore': 'true'
                        }}
                    InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="whatsapp_opec_field"
                        className={classes.itemInput}
                        label="WhatsApp OPEC" size="small" color="primary" variant="outlined"
                        value={maskPhone(company.whatsappOpec)}
                        onChange={({ target }) => setCompany({ ...company, whatsappOpec: unMask(target.value) })}
                        inputProps={{
                            maxLength: 16,
                            'data-lpignore': 'true',
                            'data-1password': 'true',
                            'data-op-ignore': 'true'
                        }}
                    InputLabelProps={{ shrink: true }}
                    />
                </Grid>

                <SubsectionTitle>Comercial</SubsectionTitle>

                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="email_commercial_field"
                        className={classes.itemInput}
                        label="Email Comercial" size="small" color="primary" variant="outlined"
                        value={company.emailCommercial}
                        onChange={({ target }) => setCompany({ ...company, emailCommercial: target.value })}
                        inputProps={{
                            'data-lpignore': 'true',
                            'data-1password': 'true',
                            'data-op-ignore': 'true'
                        }}
                    InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="whatsapp_commercial_field"
                        className={classes.itemInput}
                        label="WhatsApp Comercial" size="small" color="primary" variant="outlined"
                        value={maskPhone(company?.whatsappCommercial)}
                        onChange={({ target }) => setCompany({ ...company, whatsappCommercial: unMask(target.value) })}
                        inputProps={{
                            maxLength: 16,
                            'data-lpignore': 'true',
                            'data-1password': 'true',
                            'data-op-ignore': 'true'
                        }}
                    InputLabelProps={{ shrink: true }}
                    />
                </Grid>

                <SubsectionTitle>Financeiro</SubsectionTitle>

                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="payer_email_field"
                        className={classes.itemInput}
                        label="Email do financeiro" size="small" color="primary" variant="outlined"
                        value={company.payerEmail}
                        onChange={({ target }) => setCompany({ ...company, payerEmail: target.value })}
                        inputProps={{
                            'data-lpignore': 'true',
                            'data-1password': 'true',
                            'data-op-ignore': 'true'
                        }}
                    InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="payer_phone_field"
                        className={classes.itemInput}
                        label="WhatsApp do Financeiro" size="small" color="primary" variant="outlined"
                        value={maskPhone(company.payerPhoneNumber)}
                        onChange={({ target }) => setCompany({ ...company, payerPhoneNumber: unMask(target.value) })}
                        inputProps={{
                            maxLength: 16,
                            'data-lpignore': 'true',
                            'data-1password': 'true',
                            'data-op-ignore': 'true'
                        }}
                    InputLabelProps={{ shrink: true }}
                    />
                </Grid>

                <SectionTitle>Sobre a Rádio</SectionTitle>

                <Grid item xs={12} sm={12} className={classes.dialogItemInput}>
                    <TextField
                        id="about_content_field"
                        type="textarea"
                        className={classes.itemInput}
                        label="Sobre a rádio" size="small" color="primary" variant="outlined"
                        value={company.aboutContent}
                        onChange={({ target }) => setCompany({ ...company, aboutContent: target.value })}
                        multiline rows={4}
                    />
                </Grid>

                <SectionTitle>Redes Sociais e Site</SectionTitle>

                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="site_field"
                        className={classes.itemInput}
                        label="Site" size="small" color="primary" variant="outlined"
                        value={company.site}
                        placeholder="loopert.com"
                        onChange={({ target }) => setCompany({ ...company, site: target.value })}
                        onBlur={handleSiteBlur}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="whatsapp_field"
                        className={classes.itemInput}
                        label="Whatsapp da rádio" size="small" color="primary" variant="outlined"
                        value={maskPhone(company?.publicWhatsappPhoneNumber)}
                        onChange={({ target }) => setCompany({ ...company, publicWhatsappPhoneNumber: unMask(target.value) })}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="twitter_field"
                        className={classes.itemInput}
                        label="Twitter" size="small" color="primary" variant="outlined"
                        value={company.twitterLink}
                        placeholder="Loopert Inc"
                        onChange={({ target }) => setCompany({ ...company, twitterLink: target.value })}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="instagram_field"
                        className={classes.itemInput}
                        label="Instagram" size="small" color="primary" variant="outlined"
                        value={company.instagramLink}
                        placeholder="Loopert Inc"
                        onChange={({ target }) => setCompany({ ...company, instagramLink: target.value })}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="facebook_field"
                        className={classes.itemInput}
                        label="Facebook" size="small" color="primary" variant="outlined"
                        value={company.facebookLink}
                        placeholder="Loopert Inc"
                        onChange={({ target }) => setCompany({ ...company, facebookLink: target.value })}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="youtube_field"
                        className={classes.itemInput}
                        label="YouTube" size="small" color="primary" variant="outlined"
                        value={company.youtubeLink}
                        placeholder="Loopert Inc"
                        onChange={({ target }) => setCompany({ ...company, youtubeLink: target.value })}
                    />
                </Grid>

                <SectionTitle>Endereço</SectionTitle>

                <Grid item xs={12} className={classes.dialogItemInput}>
                    <TextField
                        id="zipcode_field"
                        className={classes.itemInput}
                        label="CEP" size="small" color="primary" variant="outlined"
                        value={company?.address?.zipcode}
                        placeholder="99999999"
                        onChange={({ target }) => handleZipCodeInput(String(target.value))}
                        inputProps={{
                            'data-lpignore': 'true',
                            'data-1password': 'true',
                            'data-op-ignore': 'true'
                        }}
                    />
                </Grid>

                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <FormControl variant="outlined" className={classes.formControl} size='small'>
                        <InputLabel id="state_select_label">Estado</InputLabel>
                        <Select
                            id="state_select_field"
                            value={company?.address?.state}
                            onChange={({ target }) => setCompany({ ...company, address: { ...company?.address, city: '', state: String(target.value) } })}
                            variant="outlined"
                            color="primary"
                            label="Estado"
                            labelId="state_select_label"
                        >
                            {
                                statesAndCities.map((state: any) => (
                                    <MenuItem key={state.name} value={state.name}>{state.name}</MenuItem>
                                ))
                            }
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <FormControl variant="outlined" className={classes.formControl} size='small' disabled={!company?.address?.state}>
                        <InputLabel id="city_select_label">Cidade</InputLabel>
                        <Select
                            id="city_select_field"
                            value={company?.address?.city || ''}
                            onChange={({ target }) => setCompany({ ...company, address: { ...company?.address, city: String(target.value) } })}
                            variant="outlined"
                            color="primary"
                            label="Cidade"
                            labelId="city_select_label"
                        >
                            {
                                (statesAndCities.filter(state => state.name === company?.address?.state)[0]?.cities || []).map((cityName: string) => (
                                    <MenuItem key={cityName} value={cityName}>{cityName}</MenuItem>
                                ))
                            }
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="neighborhood_field"
                        className={classes.itemInput}
                        label="Bairro" size="small" color="primary" variant="outlined"
                        value={company?.address?.neighborhood}
                        onChange={({ target }) => setCompany({ ...company, address: { ...company?.address, neighborhood: target.value } })}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="street_field"
                        className={classes.itemInput}
                        label="Rua" size="small" color="primary" variant="outlined"
                        value={company?.address?.street}
                        onChange={({ target }) => setCompany({ ...company, address: { ...company?.address, street: target.value } })}
                        inputProps={{
                            'data-lpignore': 'true',
                            'data-1password': 'true',
                            'data-op-ignore': 'true'
                        }}
                    />
                </Grid>

                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="number_field"
                        className={classes.itemInput}
                        label="Número" size="small" color="primary" variant="outlined"
                        value={company?.address?.number}
                        onChange={({ target }) => setCompany({ ...company, address: { ...company?.address, number: target.value } })}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="complement_field"
                        className={classes.itemInput}
                        label="Complemento" size="small" color="primary" variant="outlined"
                        value={company?.address?.complement}
                        onChange={({ target }) => setCompany({ ...company, address: { ...company?.address, complement: target.value } })}
                    />
                </Grid>

                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="latitude_field"
                        className={classes.itemInput}
                        label="Latitude"
                        size="small"
                        color="primary"
                        variant="outlined"
                        value={company?.address?.latitude || ''}
                        onChange={({ target }) => setCompany({ ...company, address: { ...company?.address, latitude: target.value } })}
                        InputLabelProps={{
                            shrink: !!company?.address?.latitude
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                    <TextField
                        id="longitude_field"
                        className={classes.itemInput}
                        label="Longitude"
                        size="small"
                        color="primary"
                        variant="outlined"
                        value={company?.address?.longitude || ''}
                        onChange={({ target }) => setCompany({ ...company, address: { ...company?.address, longitude: target.value } })}
                        InputLabelProps={{
                            shrink: !!company?.address?.longitude
                        }}
                    />
                </Grid>

                <SectionTitle>Cobertura</SectionTitle>

                <Grid item xs={12} sm={12} className={classes.dialogItemInput}>
                    <FormControl variant="outlined" className={classes.formControl} size='small'>
                        <CitySearchSelect
                            value={company?.coveredCities || []}
                            onChange={(value) => handleCityChange(value)}
                            onStarCity={handleStarClick}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} className={classes.dialogItemInput}>
                    <TextField
                        id="maps_zoom_field"
                        className={classes.itemInput}
                        type='number'
                        label="Zoom" size="small" color="primary" variant="outlined"
                        value={company?.mapsZoom}
                        onChange={({ target }) => setCompany({ ...company, mapsZoom: Number(target.value) })}
                        inputProps={{
                            type: 'number',
                            step: .1,
                            min: 0,
                            max: 12
                        }}
                        defaultValue={4}
                    />
                </Grid>

            </Grid>

            <Divider size={2} />

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    id="cancel_company_button"
                    onClick={onClose}
                    color="primary"
                >
                    Cancelar
                </Button>
                <Button
                    id="save_company_button"
                    onClick={onSave}
                    color="primary"
                    autoFocus
                    variant="contained"
                    disabled={loading}
                    endIcon={loading && (
                        <CircularProgress size={20} color="inherit" />
                    )}
                >
                    {company.uuid ? 'Salvar' : 'Criar'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default CompanyFormDialog
